import React, { useState } from 'react';
import { Copy, Check, Tag, ArrowUpRight, Contact, MapPin } from 'lucide-react';
export type TabId = 'social' | 'personal' | 'notes';
interface InfluencerProfileProps {
  activeSection?: string | null;
  activeTab?: TabId;
  onTabChange?: (tab: TabId) => void;
  onSectionClick?: (section: string) => void;
}
export const InfluencerProfile: React.FC<InfluencerProfileProps> = ({
  activeSection,
  activeTab: controlledTab,
  onTabChange,
  onSectionClick
}) => {
  const [copied, setCopied] = useState(false);
  const [internalTab, setInternalTab] = useState<TabId>('social');
  const activeTab = controlledTab ?? internalTab;
  const setActiveTab = (tab: TabId) => {
    setInternalTab(tab);
    onTabChange?.(tab);
  };
  const handleCopy = () => {
    const email = 'john@abctalentagency.com';
    if (navigator.clipboard) {
      navigator.clipboard.writeText(email).catch(() => {});
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };
  // When a left-menu section is active, lift the matching region into a white
  // glassy popup and blur/dim everything else.
  const getHighlightClass = (section: string) => {
    if (!activeSection) return 'transition-all duration-300 rounded-lg';
    return activeSection === section ? 'relative z-20 bg-white/80 backdrop-blur-md rounded-xl ring-1 ring-white/60 shadow-[0_8px_30px_rgba(0,0,0,0.12)] p-3 -m-1 scale-[1.02] transition-all duration-300' : 'blur-[2px] opacity-40 transition-all duration-300';
  };
  const tabs: {
    id: TabId;
    label: string;
  }[] = [{
    id: 'social',
    label: 'Social Media'
  }, {
    id: 'personal',
    label: 'Personal Information'
  }, {
    id: 'notes',
    label: 'Notes'
  }];
  return <div className="w-full bg-white rounded-xl border border-gray-200 shadow-float overflow-hidden text-left font-sans text-sm" data-id="element-599">
      {/* Header */}
      <div className="p-4 md:p-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-gray-100 relative" data-id="element-600">
        <div className={`flex items-center gap-4 ${getHighlightClass('influencer')}`} onClick={() => onSectionClick?.('influencer')} data-id="element-601">
          <img src="https://cdn.magicpatterns.com/patterns/figma-images/HPPMfQF9zbBNm5oHh69dtz/3867-596.png" alt="Michelle Brown" className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover" data-id="element-602" />
          <div data-id="element-603">
            <h3 className="text-xl font-bold text-gray-900 mb-1" data-id="element-604">
              Michelle Brown
            </h3>
            <div className="flex items-center gap-2 mb-1" data-id="element-605">
              <img src="https://cdn.magicpatterns.com/patterns/figma-images/HPPMfQF9zbBNm5oHh69dtz/362-1248.png" alt="TikTok" className="w-3 h-3" data-id="element-606" />
              <img src="https://cdn.magicpatterns.com/patterns/figma-images/HPPMfQF9zbBNm5oHh69dtz/362-1249.png" alt="Instagram" className="w-3 h-3" data-id="element-607" />
            </div>
            <p className="text-xs text-gray-600 mb-2" data-id="element-608">
              Paid Brand Promo • Affiliate
            </p>
            <div className="flex flex-wrap gap-1.5" data-id="element-609">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[#FBEFC8] text-[#BE8725] text-[10px] font-medium" data-id="element-610">
                <img src="https://cdn.magicpatterns.com/patterns/figma-images/HPPMfQF9zbBNm5oHh69dtz/3867-579.png" alt="US Flag" className="w-3 h-2 rounded-sm" data-id="element-611" />
                United States
              </span>
              <span className="inline-flex items-center px-2 py-0.5 rounded bg-[#FCE1C8] text-[#EB7535] text-[10px] font-medium" data-id="element-612">
                Influencer
              </span>
              <span className="inline-flex items-center px-2 py-0.5 rounded bg-[#EAD2EE] text-[#A971B1] text-[10px] font-medium" data-id="element-613">
                Fashion
              </span>
            </div>
          </div>
        </div>

        <div className={`text-left md:text-right bg-gray-50 p-3 rounded-lg border border-gray-100 relative ${getHighlightClass('agency')}`} onClick={() => onSectionClick?.('agency')} data-id="element-614">
          {/* Highlight indicator for CTA */}
          <div className="absolute -top-3 -right-3 bg-zano-pink text-white text-[10px] font-bold px-2 py-1 rounded-full shadow-md flex items-center gap-1 animate-bounce" data-id="element-615">
            Contact Manager <ArrowUpRight size={12} data-id="element-616" />
          </div>

          <h4 className="text-base font-bold text-gray-900 mb-1" data-id="element-617">
            ABC Talent Agency
          </h4>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-semibold mb-1.5" data-id="element-618">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-619" />
            Exclusive representation
          </span>
          <p className="text-xs text-gray-600 mb-0.5" data-id="element-620">John Marcus</p>
          <p className="text-xs text-gray-600 mb-0.5" data-id="element-621">+1-818-444-2311</p>
          <div className="text-xs text-gray-600 flex items-center md:justify-end gap-1.5 mt-1" data-id="element-622">
            <span className="font-medium text-zano-charcoal" data-id="element-623">
              john@abctalentagency.com
            </span>
            <button onClick={handleCopy} aria-label="Copy agency email" className={`p-1 rounded-md transition-colors ${copied ? 'bg-green-100 text-green-600' : 'bg-white border border-gray-200 text-gray-500 hover:text-zano-pink hover:border-zano-pink'}`} data-id="element-624">
              {copied ? <Check size={12} data-id="element-625" /> : <Copy size={12} data-id="element-626" />}
            </button>
          </div>
          {copied && <p className="text-[10px] text-green-600 font-medium mt-0.5 md:text-right absolute right-3 -bottom-4" data-id="element-627">
              Copied!
            </p>}
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-gray-50/50 border-b border-gray-100 px-4 md:px-6 flex gap-6 overflow-x-auto" data-id="element-628">
        {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} aria-pressed={activeTab === tab.id} className={`py-3 text-xs whitespace-nowrap uppercase tracking-wide border-b-2 transition-colors ${activeTab === tab.id ? 'font-bold text-zano-pink border-zano-pink' : 'font-semibold text-gray-500 border-transparent hover:text-gray-700'}`} data-id="element-629">
            {tab.label}
          </button>)}
      </div>

      {/* Personal Information tab */}
      {activeTab === 'personal' && <div className="p-4 md:p-6 space-y-6" data-id="element-630">
          <div data-id="element-631">
            <div className="flex items-center gap-1.5 mb-3" data-id="element-632">
              <Contact size={14} className="text-gray-800" data-id="element-633" />
              <h4 className="text-sm font-semibold text-gray-900" data-id="element-634">
                Basic Info
              </h4>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 border-t border-gray-100 pt-3" data-id="element-635">
              <div data-id="element-636">
                <p className="text-[10px] text-gray-500 mb-0.5" data-id="element-637">Name</p>
                <p className="text-xs font-medium text-gray-900" data-id="element-638">
                  Marsha Glonen
                </p>
              </div>
              <div data-id="element-639">
                <p className="text-[10px] text-gray-500 mb-0.5" data-id="element-640">Languages</p>
                <p className="text-xs font-medium text-gray-900" data-id="element-641">
                  English, Spanish
                </p>
              </div>
              <div data-id="element-642">
                <p className="text-[10px] text-gray-500 mb-0.5" data-id="element-643">Gender</p>
                <p className="text-xs font-medium text-gray-900" data-id="element-644">Female</p>
              </div>
              <div data-id="element-645">
                <p className="text-[10px] text-gray-500 mb-0.5" data-id="element-646">Age</p>
                <p className="text-xs font-medium text-gray-900" data-id="element-647">28</p>
              </div>
            </div>
          </div>
          <div data-id="element-648">
            <div className="flex items-center gap-1.5 mb-3" data-id="element-649">
              <MapPin size={14} className="text-gray-800" data-id="element-650" />
              <h4 className="text-sm font-semibold text-gray-900" data-id="element-651">Location</h4>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 border-t border-gray-100 pt-3" data-id="element-652">
              <div data-id="element-653">
                <p className="text-[10px] text-gray-500 mb-0.5" data-id="element-654">Country</p>
                <p className="text-xs font-medium text-gray-900" data-id="element-655">
                  United States
                </p>
              </div>
              <div data-id="element-656">
                <p className="text-[10px] text-gray-500 mb-0.5" data-id="element-657">State</p>
                <p className="text-xs font-medium text-gray-900" data-id="element-658">California</p>
              </div>
              <div data-id="element-659">
                <p className="text-[10px] text-gray-500 mb-0.5" data-id="element-660">City</p>
                <p className="text-xs font-medium text-gray-900" data-id="element-661">Los Angeles</p>
              </div>
            </div>
          </div>
          <p className="text-[10px] text-gray-400 italic" data-id="element-662">
            Personal information is filled out and maintained by the talent
            agency.
          </p>
        </div>}

      {/* Notes tab */}
      {activeTab === 'notes' && <div className="p-4 md:p-6" data-id="element-663">
          <div className="bg-gray-50 rounded-lg border border-gray-100 p-4 space-y-3" data-id="element-664">
            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-665">
              Talent manager notes
            </p>
            <p className="text-xs text-gray-700 leading-relaxed" data-id="element-666">
              Michelle Brown is a Los Angeles-based fashion and lifestyle
              creator known for her polished try-on hauls and authentic GRWM
              content. She over-indexes with a Gen-Z and young-millennial female
              audience in the US and UK, and consistently drives strong TikTok
              Shop conversion.
            </p>
            <div data-id="element-667">
              <p className="text-[11px] font-semibold text-gray-800 mb-1" data-id="element-668">
                Notable collaborations
              </p>
              <p className="text-xs text-gray-700 leading-relaxed" data-id="element-669">
                Revolve (summer capsule launch), Sephora Squad, Fashion Nova,
                and a recurring ambassador partnership with a DTC skincare brand
                across 3 campaigns.
              </p>
            </div>
            <div data-id="element-670">
              <p className="text-[11px] font-semibold text-gray-800 mb-1" data-id="element-671">
                Awards and recognition
              </p>
              <p className="text-xs text-gray-700 leading-relaxed" data-id="element-672">
                Finalist, 2024 Creator Economy Awards (Fashion). Featured in
                TikTok Rising Creators to Watch list.
              </p>
            </div>
            <div data-id="element-673">
              <p className="text-[11px] font-semibold text-gray-800 mb-1" data-id="element-674">
                Searchable keywords
              </p>
              <div className="flex flex-wrap gap-1.5" data-id="element-675">
                {['fashion', 'try-on haul', 'GRWM', 'TikTok Shop', 'affiliate', 'skincare', 'Gen-Z', 'US', 'UK', 'brand ambassador'].map(kw => <span key={kw} className="px-2 py-0.5 bg-white border border-gray-200 text-gray-600 rounded text-[10px] font-medium" data-id="element-676">
                    {kw}
                  </span>)}
              </div>
            </div>
            <p className="text-[10px] text-gray-400 italic pt-1" data-id="element-677">
              Notes are written by the talent manager. Keywords here power
              search when brands and agencies query the database.
            </p>
          </div>
        </div>}

      {/* Social Media tab */}
      {activeTab === 'social' && <div className="p-4 md:p-6 space-y-6" data-id="element-678">
          {/* Tags */}
          <div className={getHighlightClass('influencer')} onClick={() => onSectionClick?.('influencer')} data-id="element-679">
            <div className="flex items-center gap-1.5 mb-2" data-id="element-680">
              <Tag size={14} className="text-gray-800" data-id="element-681" />
              <h4 className="text-sm font-semibold text-gray-900" data-id="element-682">
                Content Style
              </h4>
            </div>
            <div className="flex flex-wrap gap-1.5" data-id="element-683">
              {['TikTok Shop', 'GRWM', 'Try-on Hauls'].map(tag => <span key={tag} className="px-2 py-1 bg-blue-50 text-blue-600 rounded text-xs font-medium" data-id="element-684">
                  {tag}
                </span>)}
            </div>
          </div>

          <hr className="border-gray-100" data-id="element-685" />

          {/* TikTok Section */}
          <section className={getHighlightClass('analytics')} onClick={() => onSectionClick?.('analytics')} data-id="element-686">
            <div className="flex items-center justify-between mb-4" data-id="element-687">
              <div className="flex items-center gap-1.5" data-id="element-688">
                <img src="https://cdn.magicpatterns.com/patterns/figma-images/HPPMfQF9zbBNm5oHh69dtz/362-1248.png" alt="TikTok" className="w-4 h-4" data-id="element-689" />
                <h4 className="text-sm font-semibold text-gray-900" data-id="element-690">TikTok</h4>
              </div>
              <span className="text-xs text-gray-600" data-id="element-691">@michellethereal</span>
            </div>

            <div className="space-y-6" data-id="element-692">
              {/* Analytics */}
              <div data-id="element-693">
                <h5 className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2" data-id="element-694">
                  Live Analytics
                </h5>
                <div className="grid grid-cols-4 gap-2" data-id="element-695">
                  <div data-id="element-696">
                    <p className="text-[10px] text-gray-500 mb-0.5" data-id="element-697">
                      Followers
                    </p>
                    <p className="text-sm font-semibold text-gray-900" data-id="element-698">2.2M</p>
                  </div>
                  <div data-id="element-699">
                    <p className="text-[10px] text-gray-500 mb-0.5" data-id="element-700">
                      AVG Views
                    </p>
                    <p className="text-sm font-semibold text-gray-900" data-id="element-701">50K</p>
                  </div>
                  <div data-id="element-702">
                    <p className="text-[10px] text-gray-500 mb-0.5" data-id="element-703">
                      AVG Likes
                    </p>
                    <p className="text-sm font-semibold text-gray-900" data-id="element-704">4.5K</p>
                  </div>
                  <div data-id="element-705">
                    <p className="text-[10px] text-gray-500 mb-0.5" data-id="element-706">ENG Rate</p>
                    <p className="text-sm font-semibold text-gray-900" data-id="element-707">5.3%</p>
                  </div>
                </div>
              </div>

              {/* Demographics */}
              <div data-id="element-708">
                <h5 className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2" data-id="element-709">
                  Audience Demographics
                </h5>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-710">
                  {/* Gender */}
                  <div data-id="element-711">
                    <p className="text-[10px] text-gray-500 mb-2" data-id="element-712">Gender</p>
                    <div className="flex items-center gap-3" data-id="element-713">
                      <div className="relative w-16 h-16 rounded-full shrink-0" style={{
                    background: 'conic-gradient(#77CCEA 0% 50%, #FF9CBE 50% 100%)'
                  }} data-id="element-714">
                        <div className="absolute inset-[8px] bg-white rounded-full" data-id="element-715"></div>
                      </div>
                      <div className="space-y-1 text-xs w-full" data-id="element-716">
                        <div className="flex items-center justify-between gap-2" data-id="element-717">
                          <div className="flex items-center gap-1.5" data-id="element-718">
                            <span className="w-2 h-2 rounded-full bg-[#77CCEA]" data-id="element-719"></span>
                            <span className="text-gray-600" data-id="element-720">Male</span>
                          </div>
                          <span className="font-medium" data-id="element-721">50%</span>
                        </div>
                        <div className="flex items-center justify-between gap-2" data-id="element-722">
                          <div className="flex items-center gap-1.5" data-id="element-723">
                            <span className="w-2 h-2 rounded-full bg-[#FF9CBE]" data-id="element-724"></span>
                            <span className="text-gray-600" data-id="element-725">Female</span>
                          </div>
                          <span className="font-medium" data-id="element-726">50%</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Country */}
                  <div data-id="element-727">
                    <p className="text-[10px] text-gray-500 mb-2" data-id="element-728">Country</p>
                    <div className="space-y-2" data-id="element-729">
                      {[{
                    name: '🇺🇸 US',
                    val: 60
                  }, {
                    name: '🇬🇧 UK',
                    val: 30
                  }, {
                    name: '🇩🇪 DE',
                    val: 10
                  }].map(c => <div key={c.name} data-id="element-730">
                          <div className="flex justify-between text-[10px] mb-0.5" data-id="element-731">
                            <span className="text-gray-700" data-id="element-732">{c.name}</span>
                            <span className="font-medium" data-id="element-733">{c.val}%</span>
                          </div>
                          <div className="h-1 w-full bg-gray-100 rounded-full overflow-hidden" data-id="element-734">
                            <div className="h-full bg-[#E66792] rounded-full" style={{
                        width: `${c.val}%`
                      }} data-id="element-735"></div>
                          </div>
                        </div>)}
                    </div>
                  </div>

                  {/* Age */}
                  <div data-id="element-736">
                    <p className="text-[10px] text-gray-500 mb-2" data-id="element-737">Age</p>
                    <div className="h-20 flex items-end gap-1.5 pb-4 border-b border-gray-200 relative" data-id="element-738">
                      {[{
                    label: '13-17',
                    val: 4
                  }, {
                    label: '18-24',
                    val: 40
                  }, {
                    label: '25-34',
                    val: 20
                  }, {
                    label: '35-44',
                    val: 10
                  }, {
                    label: '45+',
                    val: 9
                  }].map(a => <div key={a.label} className="flex-1 flex flex-col items-center gap-0.5" data-id="element-739">
                          <span className="text-[8px] font-medium text-gray-600" data-id="element-740">
                            {a.val}%
                          </span>
                          <div className="w-full max-w-[8px] bg-[#53B7AB] rounded-t-sm" style={{
                      height: `${a.val}px`
                    }} data-id="element-741"></div>
                          <span className="absolute bottom-0 text-[8px] text-gray-500 translate-y-full pt-0.5" data-id="element-742">
                            {a.label}
                          </span>
                        </div>)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <hr className="border-gray-100" data-id="element-743" />

          {/* Pricing */}
          <div className={getHighlightClass('pricing')} onClick={() => onSectionClick?.('pricing')} data-id="element-744">
            <h5 className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2" data-id="element-745">
              Pricing & Deliverables
            </h5>
            <div className="grid grid-cols-2 gap-4 mb-3" data-id="element-746">
              <div data-id="element-747">
                <p className="text-[10px] text-gray-500 mb-0.5" data-id="element-748">Video post</p>
                <p className="text-sm font-semibold text-gray-900" data-id="element-749">
                  $800 - $1,300
                </p>
              </div>
              <div data-id="element-750">
                <p className="text-[10px] text-gray-500 mb-0.5" data-id="element-751">Link in Bio</p>
                <p className="text-sm font-semibold text-gray-900" data-id="element-752">$500</p>
              </div>
            </div>
            <div className="bg-gray-50 rounded p-3 border border-gray-100" data-id="element-753">
              <p className="text-[10px] font-semibold text-gray-500 mb-1" data-id="element-754">
                Agency Notes
              </p>
              <p className="text-xs text-gray-700 whitespace-pre-line leading-relaxed" data-id="element-755">
                Script - additional $400{'\n'}
                price includes 2 revisions{'\n'}
                additional revisions + $100 each{'\n'}7 days lead time, link in
                bio price for 24 hrs
              </p>
            </div>
          </div>
        </div>}
    </div>;
};